/**
 * Pre-Tied Bow Tie Fee Handler
 *
 * Automatically adds/removes a $5 fee product when customer selects Pre-Tied option
 * for bow tie products.
 *
 * Product ID: 10073387696442 (Pre-Tied Service Fee - $5.00)
 */

(function() {
  'use strict';

  // Update this to your fee product's handle (URL slug)
  // Find it at: Admin > Products > Pre-Tied Service Fee > look at URL
  const FEE_PRODUCT_HANDLE = 'pre-tied-service-fee';
  const FEE_VARIANT_ID = null; // Will be fetched from product

  class BowTieFeeHandler {
    constructor() {
      this.dropdown = document.getElementById('bow-tie-style');
      this.productFormDiv = document.querySelector('[data-product-form]');
      this.productForm = this.productFormDiv ? this.productFormDiv.querySelector('form') : null;
      this.submitButton = this.productForm ? this.productForm.querySelector('[data-add-to-cart-trigger]') : null;
      this.feeVariantId = null;
      this.uniqueId = null;
      this.isProcessing = false;

      if (!this.dropdown) {
        return;
      }
      if (!this.productForm) {
        return;
      }

      // Attach the submit handler IMMEDIATELY (before async init)
      // This ensures the handler is ready even while fee variant ID loads
      const boundHandler = this.handleFormSubmit.bind(this);
      this.productForm.addEventListener('submit', boundHandler, {capture: true});

      // ALSO attach to button click as a failsafe (runs before form submit)
      if (this.submitButton) {
        const boundClickHandler = this.handleButtonClick.bind(this);
        this.submitButton.addEventListener('click', boundClickHandler, {capture: true});
      }

      // Now run async initialization
      this.init();
    }

    async init() {
      // Fetch the variant ID for the fee product
      await this.fetchFeeVariantId();

      // Generate unique ID for linking cart items
      this.uniqueId = this.generateUniqueId();

      // Listen to dropdown changes
      this.dropdown.addEventListener('change', this.handleStyleChange.bind(this));
    }

    async fetchFeeVariantId() {
      try {
        const response = await fetch(`/products/${FEE_PRODUCT_HANDLE}.js`);
        const product = await response.json();

        if (product && product.variants && product.variants.length > 0) {
          this.feeVariantId = product.variants[0].id;
        }
      } catch (error) {
        // Silently fail - error will be caught when trying to add fee
      }
    }

    generateUniqueId() {
      return 'bt-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    }

    handleStyleChange(event) {
      const selectedStyle = event.target.value;

      if (selectedStyle === 'Pre-Tied') {
        // Fee will be added during form submission
        // Visual feedback could be added here
        this.showFeeNotice(true);
      } else {
        // Remove fee from cart if it exists
        this.removeFeeFromCart();
        this.showFeeNotice(false);
      }
    }

    handleButtonClick(event) {
      const selectedStyle = this.dropdown.value;

      if (selectedStyle === 'Pre-Tied') {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        this.addBothToCart();
      }
    }

    handleFormSubmit(event) {
      const selectedStyle = this.dropdown.value;

      if (selectedStyle === 'Pre-Tied') {
        // Prevent default to add both products
        event.preventDefault();
        // Stop other handlers (like AJAX cart) from running
        event.stopImmediatePropagation();
        event.stopPropagation();

        this.addBothToCart();
      }
    }

    async addBothToCart() {
      if (this.isProcessing) {
        return;
      }

      if (!this.feeVariantId) {
        alert('Error: Fee product not loaded. Please refresh the page and try again.');
        return;
      }

      this.isProcessing = true;

      // Disable button and start animation
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('disabled');
      const buttonSpans = this.submitButton.querySelectorAll('span');
      buttonSpans.forEach(span => {
        span.classList.remove('fadeInDown');
        span.classList.add('animated', 'zoomOut');
      });

      try {
        // Get main product variant ID from form
        const formData = new FormData(this.productForm);
        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);
        const bowTieStyle = formData.get('properties[Bow Tie Style]');

        // Get the product title from the page
        const productTitle = document.querySelector('.product-title, h1.title, [itemprop="name"]')?.textContent?.trim() || 'Bow Tie';

        // Prepare items to add
        const items = [
          {
            id: mainVariantId,
            quantity: quantity,
            properties: {
              'Bow Tie Style': bowTieStyle,
              '_linkedId': this.uniqueId
            }
          },
          {
            id: this.feeVariantId,
            quantity: quantity,
            properties: {
              '_linkedId': this.uniqueId,
              '_isFee': 'Pre-Tied Service Fee',
              'For': productTitle
            }
          }
        ];

        // Add both items to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          throw new Error('Failed to add items to cart');
        }

        const result = await response.json();

        // Show checkmark animation
        const checkmark = this.submitButton.querySelector('.checkmark');
        if (checkmark) {
          checkmark.classList.add('checkmark-active');
        }

        // After 1 second, restore button and trigger cart display
        setTimeout(() => {
          // Remove disabled state
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('disabled');

          // Remove checkmark
          if (checkmark) {
            checkmark.classList.remove('checkmark-active');
          }

          // Restore button text with fadeIn animation
          const textAndIcon = this.submitButton.querySelectorAll('.text, .icon');
          textAndIcon.forEach(el => {
            el.classList.remove('zoomOut');
            el.classList.add('fadeInDown');
          });

          // Trigger cart update after animation
          const addedToCart = () => {
            const buttonSpans = this.submitButton.querySelectorAll('span');
            buttonSpans.forEach(span => span.classList.remove('fadeInDown'));
            this.triggerCartUpdate();
          };

          // Wait for animation to end, then show cart
          this.submitButton.addEventListener('animationend', addedToCart, { once: true });
          // Fallback if no animation
          setTimeout(addedToCart, 500);

        }, 1000);

      } catch (error) {
        alert('There was an error adding the item to your cart. Please try again.');
        // Restore button on error
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('disabled');
      } finally {
        this.isProcessing = false;
      }
    }

    async removeFeeFromCart() {
      try {
        // Get current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find fee items with matching linkedId
        const feeItems = cart.items.filter(item =>
          item.properties &&
          item.properties._linkedId === this.uniqueId &&
          item.properties._isFee
        );

        // Remove each fee item
        for (const item of feeItems) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        }

        if (feeItems.length > 0) {
          this.triggerCartUpdate();
        }

      } catch (error) {
        // Silently fail - fee removal is non-critical
      }
    }

    triggerCartUpdate() {
      // Different themes use different cart update methods
      // This covers common approaches

      // Method 1: Custom event
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Method 2: Check cart_action setting
      const cartAction = this.productFormDiv.dataset.cartAction ||
                         (typeof window.theme !== 'undefined' ? window.theme.cart_action : null);

      if (cartAction === 'reload_page') {
        window.location.href = '/cart';
      } else if (cartAction === 'redirect_cart') {
        window.location.href = '/cart';
      } else if (cartAction === 'mini_cart') {
        // Method 3a: Show mini cart (not drawer)
        if (typeof window.PXUTheme !== 'undefined' && window.PXUTheme.jsAjaxCart) {
          // Update the cart view first to reflect the new items
          window.PXUTheme.jsAjaxCart.updateView();
          // Wait a moment for the view to update, then show the mini cart
          setTimeout(() => {
            const $el = document.querySelector('[data-ajax-cart-trigger]');
            if ($el) {
              $el.classList.add('show-mini-cart');
            }
          }, 300);
        } else {
          this.showSuccessMessage();
        }
      } else if (cartAction === 'drawer') {
        // Method 3b: Show drawer
        if (typeof window.PXUTheme !== 'undefined' && window.PXUTheme.jsAjaxCart) {
          window.PXUTheme.jsAjaxCart.updateView();
          setTimeout(() => {
            window.PXUTheme.jsAjaxCart.showDrawer();
          }, 300);
        } else {
          this.showSuccessMessage();
        }
      } else {
        // Default fallback
        this.showSuccessMessage();
      }
    }

    showFeeNotice(show) {
      let notice = document.getElementById('pre-tied-fee-notice');

      if (show) {
        if (!notice) {
          notice = document.createElement('div');
          notice.id = 'pre-tied-fee-notice';
          notice.className = 'pre-tied-fee-notice';
          notice.innerHTML = '<strong>Note:</strong> A $5.00 pre-tied service fee will be added to your cart.';
          notice.style.cssText = 'margin: 10px 0; padding: 10px; background: #f0f8ff; border-left: 3px solid #0066cc; font-size: 14px;';

          this.dropdown.parentElement.appendChild(notice);
        }
        notice.style.display = 'block';
      } else if (notice) {
        notice.style.display = 'none';
      }
    }

    showSuccessMessage() {
      const addToCartBtn = this.productForm.querySelector('[data-add-to-cart-trigger]');
      if (addToCartBtn) {
        const originalText = addToCartBtn.querySelector('.text').textContent;
        addToCartBtn.querySelector('.text').textContent = 'Added to Cart!';
        addToCartBtn.classList.add('is-added');

        setTimeout(() => {
          addToCartBtn.querySelector('.text').textContent = originalText;
          addToCartBtn.classList.remove('is-added');
        }, 2000);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new BowTieFeeHandler();
    });
  } else {
    new BowTieFeeHandler();
  }

})();
